import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link as LinkIcon,
  Plus,
  Undo2,
  Redo2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { MAX_DESCRIPTION_LENGTH, MAX_TITLE_LENGTH } from '../constants/taskFields';
import {
  buildCreatePayload,
  completeTask,
  createTask,
  deleteTask,
  duplicateTask,
  fetchAssignees,
  fetchTaskPreferences,
  reopenTask,
  saveTaskReminderDefaults,
  updateTask,
} from '../services/taskApi';
import {
  getTimezoneAbbreviation,
  newIdempotencyKey,
  parseTaskInstant,
  plainTextFromHtml,
} from '../services/taskTimezoneUtils';
import { TaskAssociationSelector } from './TaskAssociationSelector';
import {
  TaskDateCalendar,
  TaskDateFieldButton,
  formatDisplayDate,
} from './TaskDateCalendar';
import { TaskReminderSection } from './TaskReminderSection';
import { TaskTimePicker } from './TaskTimePicker';
import {
  applyReminderPriorityDefault,
  DEFAULT_REMINDER_CONFIG,
  normalizeReminderConfig,
} from '../services/taskReminderScheduler';

export const createEmptyTaskForm = (timezone, reminderDefaults = DEFAULT_REMINDER_CONFIG) => ({
  title: '',
  descriptionHtml: '',
  showDescription: false,
  dueDate: '',
  dueTime: '',
  timezone,
  assigneeUserId: '',
  priority: 'normal',
  status: 'pending',
  associations: [],
  reminders: {
    ...normalizeReminderConfig(reminderDefaults),
    enabled: false,
    soundRepeatExplicit: false,
  },
  recurrenceEnabled: false,
  recurrence: {
    frequency: 'weekly',
    interval: 1,
    weekdays: [],
    monthday: null,
    endMode: 'never',
    count: 12,
    until: null,
  },
  version: null,
});

function combineDueAt(dueDate, dueTime, timezone) {
  if (!dueDate) return null;
  const time = dueTime || '09:00';
  // Interpret as local wall time in selected timezone via Date parsing of ISO-like string without Z,
  // then convert using temporal offset approximation: store as UTC ISO from local Date.
  const local = new Date(`${dueDate}T${time}:00`);
  if (Number.isNaN(local.getTime())) return null;
  return local.toISOString();
}

function splitDueAt(dueAt) {
  if (!dueAt) return { dueDate: '', dueTime: '' };
  const d = parseTaskInstant(dueAt);
  if (Number.isNaN(d.getTime())) return { dueDate: '', dueTime: '' };
  // Local wall time, mirroring combineDueAt (which parses the form values as
  // local time). Slicing the UTC ISO string here would shift the displayed
  // time by the viewer's UTC offset on every edit.
  const pad = (n) => String(n).padStart(2, '0');
  const dueDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const dueTime = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return { dueDate, dueTime };
}

export function TaskDrawer({
  open,
  onOpenChange,
  workspace,
  task,
  onRequestClose,
  onSaved,
}) {
  const timezone = workspace.timezone || 'UTC';
  const tzAbbr = getTimezoneAbbreviation(new Date(), timezone);
  const [form, setForm] = useState(() => createEmptyTaskForm(timezone));
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [titleError, setTitleError] = useState('');
  const [associationSelectorType, setAssociationSelectorType] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [dueCalendarOpen, setDueCalendarOpen] = useState(false);
  const [reminderDefaults, setReminderDefaults] = useState(DEFAULT_REMINDER_CONFIG);
  const titleRef = useRef(null);
  const editorRef = useRef(null);
  const associationTriggerRef = useRef(null);
  const isEdit = Boolean(task?.taskId);

  useEffect(() => {
    if (!open) return;
    if (task) {
      const { dueDate, dueTime } = splitDueAt(task.dueAt);
      setForm({
        ...createEmptyTaskForm(task.timezone || timezone),
        title: task.title || '',
        descriptionHtml: task.descriptionHtml || '',
        showDescription: Boolean(task.descriptionHtml),
        dueDate,
        dueTime,
        timezone: task.timezone || timezone,
        assigneeUserId: task.assigneeUserId || '',
        priority: task.priority || 'normal',
        status: task.status || 'pending',
        associations: task.associations || [],
        reminders: {
          ...normalizeReminderConfig(task.reminders),
          soundRepeatExplicit: true,
        },
        version: task.version,
      });
    } else {
      setForm(createEmptyTaskForm(timezone, reminderDefaults));
    }
    setDirty(false);
    setTitleError('');
    setAssociationSelectorType(null);
    setLinkUrl('');
    setShowLinkInput(false);
    setDueCalendarOpen(false);
    setTimeout(() => titleRef.current?.focus(), 50);
  }, [open, task, timezone]);

  useEffect(() => {
    if (!open) return;
    fetchAssignees(workspace)
      .then((data) => setAssignees(data.items || []))
      .catch(() => setAssignees([]));
  }, [open, workspace]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetchTaskPreferences(workspace)
      .then((data) => {
        if (cancelled) return;
        const nextDefaults = {
          ...normalizeReminderConfig(data.reminderDefaults),
          enabled: false,
          soundRepeatExplicit: false,
        };
        setReminderDefaults(nextDefaults);
        if (!task) {
          setForm((current) => ({
            ...current,
            reminders: current.reminders.enabled
              ? current.reminders
              : nextDefaults,
          }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, workspace, task]);

  useEffect(() => {
    if (form.showDescription && editorRef.current && form.descriptionHtml !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = form.descriptionHtml || '';
    }
  }, [form.showDescription, open]);

  const descriptionPlain = useMemo(
    () => plainTextFromHtml(form.descriptionHtml),
    [form.descriptionHtml],
  );

  const titleValid = form.title.trim().length > 0 && form.title.trim().length <= MAX_TITLE_LENGTH;
  const descriptionValid = descriptionPlain.length <= MAX_DESCRIPTION_LENGTH;
  const reminderDueValid = !form.reminders.enabled || Boolean(form.dueDate && form.dueTime);
  const canSave = titleValid && descriptionValid && reminderDueValid && !submitting;

  const updateField = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const validateTitle = () => {
    const trimmed = form.title.trim();
    if (!trimmed) {
      setTitleError('Title is required');
      return false;
    }
    if (trimmed.length > MAX_TITLE_LENGTH) {
      setTitleError(`Title must be at most ${MAX_TITLE_LENGTH} characters`);
      return false;
    }
    setTitleError('');
    return true;
  };

  const execEditor = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      updateField({ descriptionHtml: editorRef.current.innerHTML });
    }
  };

  const buildPayload = () => {
    const dueAt = combineDueAt(form.dueDate, form.dueTime, form.timezone);
    return buildCreatePayload({
      ...form,
      dueAt,
      descriptionHtml: form.showDescription ? form.descriptionHtml : '',
    });
  };

  const handleSave = async ({ addAnother = false } = {}) => {
    if (!validateTitle() || !descriptionValid) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await updateTask(workspace, task.taskId, {
          ...payload,
          version: form.version,
          clear_assignee: !payload.assignee_user_id,
          clear_due_at: !payload.due_at,
        });
        toast.success('Task updated');
        saveTaskReminderDefaults(workspace, form.reminders).catch(() => {
          toast.warning('Task saved, but reminder defaults could not be updated');
        });
        onSaved?.();
      } else {
        await createTask(workspace, payload, newIdempotencyKey());
        toast.success('Task created');
        const nextReminderDefaults = {
          ...form.reminders,
          enabled: false,
          soundRepeatExplicit: false,
        };
        setReminderDefaults(nextReminderDefaults);
        saveTaskReminderDefaults(workspace, nextReminderDefaults).catch(() => {
          toast.warning('Task saved, but reminder defaults could not be updated');
        });
        if (addAnother) {
          setForm(createEmptyTaskForm(timezone, nextReminderDefaults));
          setDirty(false);
          setTimeout(() => titleRef.current?.focus(), 50);
        } else {
          onSaved?.();
        }
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error('This task was modified by someone else. Refresh and try again.');
      else toast.error(err?.response?.data?.detail || 'Failed to save task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (dirty) onRequestClose?.(true);
    else onRequestClose?.(false);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) handleClose(); else onOpenChange?.(next); }}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-hidden border-border bg-background p-0 text-foreground sm:max-w-xl"
        data-testid="task-drawer"
      >
        <SheetHeader className="sticky top-0 z-10 shrink-0 border-b border-border bg-background px-4 py-4 text-left sm:px-6">
          <SheetTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            {isEdit ? 'Edit task' : 'New task'}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          <section className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="task-title"
                ref={titleRef}
                placeholder="Enter"
                value={form.title}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => updateField({ title: e.target.value })}
                onBlur={validateTitle}
                aria-invalid={Boolean(titleError)}
                data-testid="task-title-input"
              />
              {titleError ? <p className="text-xs text-destructive">{titleError}</p> : null}
            </div>

            <div className="space-y-2">
              {!form.showDescription ? (
                <Button type="button" variant="link" className="h-auto p-0" onClick={() => updateField({ showDescription: true })}>
                  Add description
                </Button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Description</Label>
                    <Button type="button" variant="ghost" size="sm" onClick={() => updateField({ showDescription: false, descriptionHtml: '' })}>
                      Remove description
                    </Button>
                  </div>
                  <div className="overflow-hidden rounded-lg border border-border bg-background">
                    <div className="flex flex-wrap gap-1 border-b border-border p-2" role="toolbar" aria-label="Description formatting">
                      {[
                        { icon: Bold, cmd: 'bold', label: 'Bold' },
                        { icon: Italic, cmd: 'italic', label: 'Italic' },
                        { icon: Underline, cmd: 'underline', label: 'Underline' },
                        { icon: Strikethrough, cmd: 'strikeThrough', label: 'Strikethrough' },
                        { icon: List, cmd: 'insertUnorderedList', label: 'Bulleted list' },
                        { icon: ListOrdered, cmd: 'insertOrderedList', label: 'Numbered list' },
                        { icon: Undo2, cmd: 'undo', label: 'Undo' },
                        { icon: Redo2, cmd: 'redo', label: 'Redo' },
                      ].map(({ icon: Icon, cmd, label }) => (
                        <Button
                          key={cmd}
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          aria-label={label}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => execEditor(cmd)}
                        >
                          <Icon className="h-4 w-4" />
                        </Button>
                      ))}
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label="Insert link"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowLinkInput((current) => !current)}
                      >
                        <LinkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    {showLinkInput ? (
                      <div className="flex gap-2 border-b border-border p-2">
                        <Input
                          value={linkUrl}
                          onChange={(event) => setLinkUrl(event.target.value)}
                          placeholder="https://example.com"
                          aria-label="Link URL"
                          className="h-8"
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={!linkUrl.trim()}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => {
                            execEditor('createLink', linkUrl.trim());
                            setLinkUrl('');
                            setShowLinkInput(false);
                          }}
                        >
                          Add
                        </Button>
                      </div>
                    ) : null}
                    <div
                      ref={editorRef}
                      contentEditable
                      role="textbox"
                      aria-multiline="true"
                      aria-label="Task description"
                      className="min-h-[120px] px-3 py-2 text-sm outline-none"
                      onInput={() => updateField({ descriptionHtml: editorRef.current?.innerHTML || '' })}
                      data-testid="task-description-editor"
                    />
                  </div>
                  <p className={cn('text-xs', descriptionPlain.length > MAX_DESCRIPTION_LENGTH ? 'text-destructive' : 'text-muted-foreground')}>
                    {descriptionPlain.length}/{MAX_DESCRIPTION_LENGTH}
                  </p>
                </div>
              )}
            </div>
          </section>

          <section className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
            <h3 className="text-sm font-semibold">Schedule</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Due date ({tzAbbr})</Label>
                <TaskDateCalendar
                  mode="single"
                  open={dueCalendarOpen}
                  onOpenChange={setDueCalendarOpen}
                  value={form.dueDate}
                  timezoneLabel={tzAbbr}
                  trigger={(
                    <TaskDateFieldButton
                      label={form.dueDate ? formatDisplayDate(form.dueDate) : ''}
                      emptyLabel="Select due date"
                      data-testid="task-due-date"
                      aria-label={`Due date (${tzAbbr})`}
                    />
                  )}
                  onClear={() => updateField({ dueDate: '' })}
                  onConfirm={(nextDate) => updateField({ dueDate: nextDate || '' })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="task-due-time">Due time</Label>
                <TaskTimePicker
                  id="task-due-time"
                  value={form.dueTime}
                  onChange={(dueTime) => updateField({ dueTime })}
                  aria-label="Due time"
                  data-testid="task-due-time"
                />
              </div>
            </div>
          </section>

          <TaskReminderSection
            reminders={form.reminders}
            hasDueDateTime={Boolean(form.dueDate && form.dueTime)}
            onChange={(reminders) => updateField({ reminders })}
          />

          <section className="rounded-xl border border-border bg-muted/20 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Setup recurring tasks</p>
                <p className="text-xs text-muted-foreground">Generate occurrences from a recurrence series</p>
              </div>
              <Switch
                checked={form.recurrenceEnabled}
                onCheckedChange={(checked) => updateField({ recurrenceEnabled: checked })}
                aria-label="Setup recurring tasks"
              />
            </div>

            {form.recurrenceEnabled ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="task-recurrence-fields">
                <div className="space-y-2">
                  <Label>Frequency</Label>
                  <Select
                    value={form.recurrence.frequency}
                    onValueChange={(frequency) => updateField({ recurrence: { ...form.recurrence, frequency } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['daily', 'weekly', 'monthly', 'yearly'].map((f) => (
                        <SelectItem key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Repeat every</Label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    value={form.recurrence.interval}
                    onChange={(e) => updateField({
                      recurrence: { ...form.recurrence, interval: Number(e.target.value) || 1 },
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ends</Label>
                  <Select
                    value={form.recurrence.endMode}
                    onValueChange={(endMode) => updateField({ recurrence: { ...form.recurrence, endMode } })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">No end</SelectItem>
                      <SelectItem value="count">After count</SelectItem>
                      <SelectItem value="until">On date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.recurrence.endMode === 'count' ? (
                  <div className="space-y-2">
                    <Label>Occurrences</Label>
                    <Input
                      type="number"
                      min={1}
                      max={500}
                      value={form.recurrence.count || 12}
                      onChange={(e) => updateField({
                        recurrence: { ...form.recurrence, count: Number(e.target.value) || 1 },
                      })}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="grid gap-3 rounded-xl border border-border bg-muted/20 p-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Assignee</Label>
              <Select
                value={form.assigneeUserId || '__unassigned__'}
                onValueChange={(value) => updateField({
                  assigneeUserId: value === '__unassigned__' ? '' : value,
                })}
              >
                <SelectTrigger data-testid="task-assignee-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {assignees.map((user) => (
                    <SelectItem key={user.userId} value={user.userId}>
                      {user.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(priority) => updateField({
                  priority,
                  reminders: applyReminderPriorityDefault(form.reminders, priority),
                })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['low', 'normal', 'high', 'urgent'].map((p) => (
                    <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="space-y-3 rounded-xl border border-border bg-muted/20 p-4">
            <Label>Associated objects</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  ref={associationTriggerRef}
                  variant="outline"
                  type="button"
                  data-testid="task-associations-menu"
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add association
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onSelect={() => setAssociationSelectorType('contact')}>
                  Contacts
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAssociationSelectorType('opportunity')}>
                  Opportunities
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => setAssociationSelectorType('company')}>
                  Companies
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex flex-wrap gap-2">
              {form.associations.map((a) => (
                <Badge key={`${a.objectType}-${a.objectId}`} variant="secondary" className="gap-1">
                  {a.displayName || a.objectId}
                  <button
                    type="button"
                    aria-label={`Remove ${a.displayName || a.objectId}`}
                    onClick={() => updateField({
                      associations: form.associations.filter(
                        (x) => !(x.objectType === a.objectType && x.objectId === a.objectId),
                      ),
                    })}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </section>

          {isEdit ? (
            <div className="flex flex-wrap gap-2 border-t border-border pt-4">
              {form.status === 'completed' ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    await reopenTask(workspace, task.taskId);
                    toast.success('Task reopened');
                    onSaved?.();
                  }}
                >
                  Reopen
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    await completeTask(workspace, task.taskId);
                    toast.success('Task completed');
                    onSaved?.();
                  }}
                >
                  Complete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  await duplicateTask(workspace, task.taskId);
                  toast.success('Task duplicated');
                  onSaved?.();
                }}
              >
                Duplicate
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={async () => {
                  await deleteTask(workspace, task.taskId);
                  toast.success('Task deleted');
                  onSaved?.();
                }}
              >
                Delete
              </Button>
            </div>
          ) : null}
        </div>

        <SheetFooter className="sticky bottom-0 z-10 flex flex-row flex-wrap items-center justify-end gap-2 border-t border-border bg-background px-4 py-4 sm:px-6">
          <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
          {!isEdit ? (
            <Button type="button" variant="secondary" disabled={!canSave} onClick={() => handleSave({ addAnother: true })}>
              Save and add another
            </Button>
          ) : null}
          <Button type="button" disabled={!canSave} onClick={() => handleSave()} data-testid="task-save-button">
            {isEdit ? 'Save changes' : 'Save'}
          </Button>
        </SheetFooter>
        <TaskAssociationSelector
          open={Boolean(associationSelectorType)}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setAssociationSelectorType(null);
          }}
          objectType={associationSelectorType}
          workspace={workspace}
          selectedAssociations={form.associations}
          returnFocusRef={associationTriggerRef}
          onApply={(selectedForType) => {
            const otherTypes = form.associations.filter(
              (association) => association.objectType !== associationSelectorType,
            );
            const unique = new Map(
              [...otherTypes, ...selectedForType].map((association) => [
                `${association.objectType}:${association.objectId}`,
                association,
              ]),
            );
            updateField({ associations: Array.from(unique.values()) });
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

export default TaskDrawer;
